import { infiniteQueryOptions, useInfiniteQuery } from '@tanstack/react-query';

import {
  type ConsoleApiClient,
  getApiClient,
  overrideOrganizationHeader,
} from '@cast/api-client';
import { useOrganizations } from '@cast/shared-organization-context-data-access';
import type { ApiResponse } from '@cast/shared-types';
import { RbacBindingStateType } from '@cast/types';
import { makeIsEnabled } from '@cast/utils';

import { roleBindingsRetryOptions } from './roleBindingsRetry';

export const RBAC_ROLE_BINDINGS_QUERY_KEY = 'rbac-role-bindings';

type RbacRoleBindingsInfiniteQueryData = ApiResponse<
  ConsoleApiClient['rbac']['listRoleBindings']
>;

export const getRbacRoleBindingsInfiniteQueryKey = (
  params: {
    organizationId?: string;
    subjectId?: string[];
  } = {}
) => {
  if (!params.organizationId) {
    return [RBAC_ROLE_BINDINGS_QUERY_KEY] as const;
  }
  if (!params.subjectId) {
    return [RBAC_ROLE_BINDINGS_QUERY_KEY, params.organizationId] as const;
  }
  return [
    RBAC_ROLE_BINDINGS_QUERY_KEY,
    params.organizationId,
    params.subjectId,
  ] as const;
};

const getRbacRoleBindingsInfiniteQueryOptions = ({
  organizationId,
  subjectId,
  enabled = true,
  shouldTriggerRetry = false,
}: {
  organizationId: string;
  subjectId?: string[];
  enabled?: boolean;
  shouldTriggerRetry?: boolean;
}) =>
  infiniteQueryOptions({
    queryKey: getRbacRoleBindingsInfiniteQueryKey({
      organizationId,
      subjectId,
    }),
    initialPageParam: '0',
    getNextPageParam: (res: RbacRoleBindingsInfiniteQueryData) => {
      const cursor = res.nextPage.cursor;
      return !cursor || cursor === res.totalCount ? undefined : cursor;
    },
    queryFn: async ({ pageParam }) => {
      const res = await getApiClient().rbac.listRoleBindings(
        {
          organizationId,
          pageLimit: '100',
          pageCursor: pageParam,
          subjectId: subjectId,
        },
        {
          headers: overrideOrganizationHeader(organizationId || undefined),
        }
      );

      return res.data;
    },
    select: (data) => data.pages.flatMap((page) => page.roleBindings ?? []),
    ...(shouldTriggerRetry && roleBindingsRetryOptions),
    refetchInterval: ({ state }) => {
      const isProcessing = state.data?.pages
        .flatMap((page) => page.roleBindings ?? [])
        .some(
          (binding) =>
            binding?.status?.[0]?.state === RbacBindingStateType.ACCEPTED
        );

      return isProcessing ? 5_000 : false;
    },
    enabled: makeIsEnabled(!!organizationId, enabled),
  });

export type UseRbacRoleBindingsInfiniteQueryArgs = {
  organizationId?: string;
  subjectId?: string[];
  enabled?: boolean;
  shouldTriggerRetry?: boolean;
};

export const useRbacRoleBindingsInfiniteQuery = ({
  organizationId,
  enabled = true,
  subjectId,
  shouldTriggerRetry,
}: UseRbacRoleBindingsInfiniteQueryArgs = {}) => {
  const { currentOrganization } = useOrganizations();

  return useInfiniteQuery(
    getRbacRoleBindingsInfiniteQueryOptions({
      organizationId: organizationId ?? currentOrganization?.id ?? '',
      subjectId,
      enabled: enabled && !!(organizationId ?? currentOrganization?.id),
      shouldTriggerRetry,
    })
  );
};
